import { Temporal } from '@imago/api-client'
import { type MimeType } from '@imago/api-client-google-workspace'
import { type EMRID, type EMRInfo, type EMRKindID, type FileID, parseEMRID, parseKind, type UserID } from '@imago/model'
import { type DocBlock } from '../documents/_Doc.ts'
import { type SimpleDriveFile, type SimpleDriveRemote, SimpleDriveV1_Documents_createAndSetContent, SimpleDriveV1_Documents_createEmpty, SimpleDriveV1_Documents_getContent, SimpleDriveV1_Documents_setContent, SimpleDriveV1_Files_createEmpty, SimpleDriveV1_Files_delete, SimpleDriveV1_Files_get, SimpleDriveV1_Files_list, SimpleDriveV1_Files_rename, SimpleDriveV1_Folders_createAndSetContent } from './SimpleDriveV1.ts'

function str_partition(haystack: string, sep: string): [string, string, string] {
	const index = haystack.indexOf(sep)
	if (index === -1) {
		return [haystack, '', '']
	}
	return [haystack.substring(0, index), sep, haystack.substring(index + sep.length)]
}

export function parseEMR(file: SimpleDriveFile) {
	const [rawTimestamp, sep, name] = str_partition(file.name!.trim(), ' ')
	if (!sep) {
		return
	}
	let timestamp
	try {
		timestamp = Temporal.Instant.from(rawTimestamp)
	} catch (e) {
		return
	}
	let kindID = name.trim() as EMRKindID

	if (!file.parents) throw new TypeError()

	return makeEMRInfo({
		patientID: file.parents[0] as UserID,
		fileID: file.id as FileID,
		timestamp,
		kindID,
		mimeType: file.mimeType! as MimeType,
	})
}

export function makeEMRInfo({patientID, fileID, timestamp, kindID, mimeType}: {
	patientID: UserID
	fileID: FileID
	timestamp: Temporal.Instant
	kindID: EMRKindID
	mimeType: MimeType
}): EMRInfo {
	return {
		ID: `${patientID}/${fileID}`,
		timestamp,
		...parseKind(kindID),
		kindID,
		mimeType,
		patientID,
		fileID,
	}
}

export async function EMRsV1_Patient_getEmail(remote: SimpleDriveRemote, folderID: UserID) {
	return (await SimpleDriveV1_Files_get(remote, folderID)).name.trim()
}

export async function EMRsV1_Files_list(remote: SimpleDriveRemote, folderID: UserID) {
	const files = (await SimpleDriveV1_Files_list(remote, folderID))
	return files.map(parseEMR).filter(EMR => EMR !== undefined).toSorted((a, b) => -Temporal.Instant.compare(a.timestamp, b.timestamp))
}

export async function EMRsV1_Files_createEmptyFile(remote: SimpleDriveRemote, folderID: UserID, timestamp: Temporal.Instant, kindID: EMRKindID, properties?: Record<string, string>): Promise<EMRInfo> {
	const file = await SimpleDriveV1_Files_createEmpty(remote, folderID, `${timestamp.toString()} ${kindID}`, properties)
	return parseEMR(file)!
}

export async function EMRsV1_Files_createEmptyDocument(remote: SimpleDriveRemote, folderID: UserID, timestamp: Temporal.Instant, kindID: EMRKindID): Promise<EMRInfo> {
	const file = await SimpleDriveV1_Documents_createEmpty(remote, folderID, `${timestamp.toString()} ${kindID}`)
	return parseEMR(file)!
}

export async function EMRsV1_Files_getDocumentContent(remote: SimpleDriveRemote, ID: EMRID): Promise<DocBlock[]> {
	const readable = await SimpleDriveV1_Documents_getContent(remote, parseEMRID(ID).fileID)
	return readable.filter(block => block.text.length >= 1)
}

export async function EMRsV1_Files_setDocumentContent(remote: SimpleDriveRemote, ID: EMRID, readable: DocBlock[]): Promise<void> {
	await SimpleDriveV1_Documents_setContent(remote, parseEMRID(ID).fileID, readable)
}

export async function EMRsV1_Files_createDocumentAndSetContent(remote: SimpleDriveRemote, folderID: UserID, timestamp: Temporal.Instant, kindID: EMRKindID, readable: DocBlock[]): Promise<EMRInfo> {
	const file = await SimpleDriveV1_Documents_createAndSetContent(remote, folderID, `${timestamp.toString()} ${kindID}`, readable)
	return parseEMR(file)!
}

export async function EMRsV1_Files_rename(remote: SimpleDriveRemote, fileID: FileID, timestamp: Temporal.Instant, kindID: EMRKindID) {
	const file = await SimpleDriveV1_Files_rename(remote, fileID, `${timestamp.round('seconds').toString()} ${kindID}`)
	return parseEMR(file)!
}

export async function EMRsV1_Files_delete(remote: SimpleDriveRemote, fileID: FileID) {
	await SimpleDriveV1_Files_delete(remote, fileID)
}

export async function EMRsV1_Files_createFolderAndSetContent(remote: SimpleDriveRemote, folderID: UserID, timestamp: Temporal.Instant, kindID: EMRKindID, content: Map<string, Blob>): Promise<EMRInfo> {
	const file = await SimpleDriveV1_Folders_createAndSetContent(remote, folderID, `${timestamp.toString()} ${kindID}`, content)
	return parseEMR(file)!
}
